/**
 * web/src/games/HalfAMapPanel.tsx — Half a Map, the co-op maze
 * (card AHA8uCHMxHHk, P3: the Games-tab panel).
 *
 * Two seats, one maze, and a projection that is role-aware rather than
 * seat-aware: the Walker's payload carries fog and their own cell and NO maze;
 * the Navigator's carries the whole maze and the exit and NO Walker. This file
 * renders whichever shape it is handed. It never scores, never decides a move,
 * never holds a maze it was not given, and never draws a walker marker on the
 * Navigator's board — there is nothing in that payload to draw one from.
 *
 * Roles swap every round. The reveal is the one bounded exception: the
 * Navigator may spend two a round, each four seconds long, and the expiry is
 * evaluated server-side at read time, so a stale position cannot be kept.
 *
 * Live updates: rather than open a second socket listener beside Nocturne's,
 * the panel re-reads on a bounded interval while a match is in play — one GET
 * every eight seconds, the same read the initial load uses, stopping on
 * settle. Same trade the Guessmate panel makes.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  HalfAMapChatLine,
  HalfAMapDirection,
  HalfAMapFogCell,
  HalfAMapMatchView,
  HalfAMapMazeView,
  HalfAMapRoundView,
} from '../../../shared/types';
import { api, session } from '../shell/bridge';
import { announcePlusRequired } from '../lib/billing';
import { Pressable } from '../ui/Pressable';
import './halfamap.css';

const BASE = '/api/game/half-a-map';
const POLL_MS = 8_000;
const CHAT_MAX = 80;
const REVEALS_PER_ROUND = 2;
const ROUNDS = 3;

interface Member {
  memberId: string;
  displayName: string;
}

/** The wire throws `Error(JSON.stringify({error:{code,message}}))` — the panel
 *  reacts to the code, never to the prose. */
function errorCode(e: unknown): string | null {
  if (!(e instanceof Error)) return null;
  try {
    return (JSON.parse(e.message) as { error?: { code?: string } })?.error?.code ?? null;
  } catch {
    return null;
  }
}

const ERROR_COPY: Record<string, string> = {
  not_paired: 'Half a Map needs both of you in the room.',
  forbidden: 'Only the Walker moves — you are the Navigator this round.',
  not_open: 'That round has moved on — reload to catch up.',
  no_reveals_left: 'Both locates are spent this round.',
  too_long: `A line can be ${CHAT_MAX} characters at most.`,
  rate_limited: 'Give that a moment, then try again.',
  not_found: 'That match is gone — start a new one.',
  plus_required: 'Half a Map is part of Pear Plus.',
};

const ARROWS: { dir: HalfAMapDirection; glyph: string; label: string }[] = [
  { dir: 'up', glyph: '↑', label: 'Up' },
  { dir: 'left', glyph: '←', label: 'Left' },
  { dir: 'right', glyph: '→', label: 'Right' },
  { dir: 'down', glyph: '↓', label: 'Down' },
];

type WallSide = 'up' | 'down' | 'left' | 'right';
const WALL_SIDES: WallSide[] = ['up', 'down', 'left', 'right'];
const SIDE_NAME: Record<WallSide, string> = { up: 'north', down: 'south', left: 'west', right: 'east' };

/** One cell's walls, drawn as solid bars rather than as a border width on the
 *  cell. A 1px-versus-2px border delta was not a legible wall at phone size, so
 *  both boards read as a blank grid (Jonathan's 2026-11-04 playtest). The bars
 *  carry their own weight and colour from the stylesheet. */
function Walls({ walls }: { walls: Record<WallSide, boolean> }) {
  return (
    <>
      {WALL_SIDES.filter(side => walls[side]).map(side => (
        <span key={side} className={`ham-wall ham-wall-${side}`} data-wall={side} aria-hidden="true" />
      ))}
    </>
  );
}

/** The Walker's board: only the cells they have felt, each with the walls they
 *  can touch, and one "you are here" marker on the cell the server says they
 *  stand on. Unseen cells are drawn as blanks with no data — the absence is
 *  the point, and the fog array simply does not carry them. */
function WalkerBoard({
  fog,
  size,
  walker,
}: {
  fog: HalfAMapFogCell[];
  size: number;
  walker: { row: number; col: number } | null;
}) {
  const seen = new Map(fog.map(c => [`${c.row},${c.col}`, c]));
  const cells = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const cell = seen.get(`${row},${col}`);
      const here = walker != null && walker.row === row && walker.col === col;
      if (!cell) {
        cells.push(
          <div key={`${row},${col}`} className="ham-cell ham-unseen" aria-hidden="true" />,
        );
        continue;
      }
      cells.push(
        <div
          key={`${row},${col}`}
          className={`ham-cell ham-seen${cell.exit ? ' ham-exit' : ''}${here ? ' ham-here-cell' : ''}`}
          data-cell={`${row},${col}`}
          data-exit={cell.exit ? 'true' : undefined}
          data-here={here ? 'true' : undefined}
        >
          <Walls walls={cell.walls} />
          {cell.glyph ? (
            <span className="ham-glyph" aria-hidden="true">
              {cell.glyph}
            </span>
          ) : null}
          {here ? (
            <span className="ham-here" role="img" aria-label="You are here">
              ●
            </span>
          ) : null}
        </div>,
      );
    }
  }
  return (
    <div
      className="ham-board ham-board-walker"
      id="ham-board"
      data-role="walker"
      style={{ gridTemplateColumns: `repeat(${size}, 1fr)` }}
      aria-label="What you can feel, one cell at a time"
    >
      {cells}
    </div>
  );
}

/** The Navigator's board: the whole maze, its walls, its start and its exit.
 *  The walker marker belongs to the Walker (and, briefly, to a reveal) — the
 *  Navigator's payload carries no position outside a reveal. */
function NavigatorBoard({ maze }: { maze: HalfAMapMazeView }) {
  const cells = [];
  for (let row = 0; row < maze.size; row++) {
    for (let col = 0; col < maze.size; col++) {
      const i = row * maze.size + col;
      const isExit = row === maze.exit.row && col === maze.exit.col;
      const isStart = row === maze.start.row && col === maze.start.col;
      const walls: Record<WallSide, boolean> = {
        up: row === 0 ? true : maze.down[(row - 1) * maze.size + col],
        down: maze.down[i],
        left: col === 0 ? true : maze.right[i - 1],
        right: maze.right[i],
      };
      cells.push(
        <div
          key={`${row},${col}`}
          className={`ham-cell ham-open${isExit ? ' ham-exit' : ''}${isStart ? ' ham-start' : ''}`}
          data-cell={`${row},${col}`}
          data-exit={isExit ? 'true' : undefined}
          data-start={isStart ? 'true' : undefined}
        >
          <Walls walls={walls} />
          {isExit ? (
            <span className="ham-exit-mark" role="img" aria-label="The exit">
              ★
            </span>
          ) : isStart ? (
            <span className="ham-start-mark" role="img" aria-label="Where they start">
              ◎
            </span>
          ) : maze.glyph[i] ? (
            <span className="ham-glyph" aria-hidden="true">
              {maze.glyph[i]}
            </span>
          ) : null}
        </div>,
      );
    }
  }
  return (
    <div
      className="ham-board ham-board-navigator"
      id="ham-board"
      data-role="navigator"
      style={{ gridTemplateColumns: `repeat(${maze.size}, 1fr)` }}
      aria-label="The whole maze, with the exit"
    >
      {cells}
    </div>
  );
}

function termLines(round: HalfAMapRoundView): string[] {
  const t = round.terms;
  if (!t) return [];
  const parts = [`${t.base} for the round`];
  if (t.extraMoves) parts.push(`${t.extraMoves} extra moves`);
  if (t.messages) parts.push(`${t.messages} lines of chat`);
  if (t.bumps) parts.push(`${t.bumps} walls`);
  if (t.locates) parts.push(`${t.locates} locates`);
  if (t.secondsOverPar) parts.push(`${t.secondsOverPar}s over par`);
  return parts;
}

export function HalfAMapPanel({ members }: { members: Member[] }) {
  const me = session();
  const [match, setMatch] = useState<HalfAMapMatchView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [gated, setGated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatDraft, setChatDraft] = useState('');
  const [moveNote, setMoveNote] = useState<string | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  /* Overlapping reads (initial, action, poll) can land out of order; every
     reload owns a generation and a stale response is discarded. */
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const data = await api<{ match: HalfAMapMatchView | null }>(`${BASE}/match`);
      if (mine !== gen.current) return;
      setMatch(data.match ?? null);
      setLoadError(false);
      setGated(false);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      const code = errorCode(e);
      if (code === 'plus_required') {
        if (mine !== gen.current) return;
        setGated(true);
        setLoadError(false);
        announcePlusRequired();
      } else if (mine === gen.current) {
        console.error('[Pear] half a map load failed', e);
        setLoadError(true);
      }
    } finally {
      if (mine === gen.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* The clock the reveal window reads — only while a reveal is live. */
  useEffect(() => {
    if (!match?.current?.reveal) return;
    const id = window.setInterval(() => setClock(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [match?.current?.reveal?.expiresAt]);

  /* Bounded re-read while a match is in play, so the partner's move and lines
     appear without either side pressing anything. Stops on settle. */
  useEffect(() => {
    if (!match || match.state !== 'playing') return;
    const id = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(id);
  }, [match?.matchId, match?.state, reload]);

  const act = async (call: () => Promise<{ match: HalfAMapMatchView }>): Promise<boolean> => {
    setBusy(true);
    try {
      const data = await call();
      setMatch(data.match);
      setError(null);
      return true;
    } catch (e) {
      const code = errorCode(e);
      setError((code && ERROR_COPY[code]) || 'That did not save — try again.');
      if (code === 'plus_required') announcePlusRequired();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const start = () => act(() => api<{ match: HalfAMapMatchView }>(`${BASE}/start`, { method: 'POST' }));
  /* A move changes the Walker's cell and, on a bump, their wall count. Both are
     in the payload, so the panel reports what the server decided rather than
     guessing: a refused move used to look exactly like a successful one. */
  const move = async (dir: HalfAMapDirection) => {
    if (!match) return;
    const before = match.current;
    setBusy(true);
    try {
      const data = await api<{ match: HalfAMapMatchView }>(`${BASE}/move`, {
        method: 'POST',
        body: { matchId: match.matchId, dir },
      });
      setMatch(data.match);
      setError(null);
      const after = data.match.current;
      if (before && after) {
        const bumped = after.bumps > before.bumps;
        const stepped =
          before.walker != null &&
          after.walker != null &&
          (before.walker.row !== after.walker.row || before.walker.col !== after.walker.col);
        if (stepped && after.walker) {
          setMoveNote(`You stepped ${dir} — now at ${after.walker.row + 1},${after.walker.col + 1}.`);
        } else if (bumped) {
          setMoveNote(`A wall to the ${SIDE_NAME[dir]} — that way is blocked.`);
        } else {
          setMoveNote(`Nothing moved to the ${SIDE_NAME[dir]}.`);
        }
      }
    } catch (e) {
      const code = errorCode(e);
      setError((code && ERROR_COPY[code]) || 'That did not save — try again.');
      if (code === 'plus_required') announcePlusRequired();
    } finally {
      setBusy(false);
    }
  };
  const reveal = () =>
    match ? act(() => api<{ match: HalfAMapMatchView }>(`${BASE}/reveal`, { method: 'POST', body: { matchId: match.matchId } })) : Promise.resolve(false);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!match || busy) return;
    const text = chatDraft.trim();
    if (!text) return;
    if (await act(() => api<{ match: HalfAMapMatchView }>(`${BASE}/message`, { method: 'POST', body: { matchId: match.matchId, text } }))) {
      setChatDraft('');
    }
  };

  const mine = members.find(m => m.memberId === me?.memberId);
  const partner = members.find(m => m.memberId !== me?.memberId);
  const myName = mine?.displayName ?? 'You';
  const nameOf = (memberId: string): string => {
    if (memberId === me?.memberId) return myName;
    if (memberId === partner?.memberId) return partner?.displayName ?? 'your partner';
    return 'someone';
  };

  const round = match?.current ?? null;
  const walkerCell = round?.walker ?? null;
  const feltCell = walkerCell
    ? (round?.fog.find(c => c.row === walkerCell.row && c.col === walkerCell.col) ?? null)
    : null;
  const feltSides = feltCell ? WALL_SIDES.filter(side => feltCell.walls[side]) : [];

  /* The move line belongs to one round: a new round starts with a clean note. */
  useEffect(() => {
    setMoveNote(null);
  }, [match?.matchId, round?.round]);
  const revealSecondsLeft = round?.reveal
    ? Math.max(0, Math.ceil((round.reveal.expiresAt - clock) / 1000))
    : null;

  return (
    <section className="game-panel half-a-map" id="half-a-map-panel" aria-labelledby="half-a-map-title">
      <h2 className="game-title" id="half-a-map-title">
        Half a Map
      </h2>
      <p className="game-hint" id="half-a-map-hint">
        One of you walks blind, the other holds the map. The Walker feels the walls and says what
        they see; the Navigator sees the whole maze and can give away the Walker's position twice a
        round. Get to the exit together, three rounds, swapping roles each time.
      </p>

      {!loaded && <p className="game-hint">Loading…</p>}

      {loaded && gated && (
        <p className="game-hint" id="ham-gated">
          Half a Map is part of Pear Plus.
        </p>
      )}

      {loaded && !gated && loadError && (
        <>
          <p className="game-hint" role="alert">
            Could not reach Half a Map just now.
          </p>
          <div className="game-actions">
            <Pressable className="pear-btn pear-btn-blush" id="btn-ham-retry" onClick={() => void reload()}>
              Try again
            </Pressable>
          </div>
        </>
      )}

      {loaded && !gated && !loadError && !match && (
        <div id="ham-entry">
          <p className="game-hint">
            Three rounds in one maze each, then the scores add up. First round you choose nothing —
            the room already knows who walks.
          </p>
          <div className="game-actions">
            <Pressable
              className="pear-btn pear-btn-mustard"
              id="btn-ham-start"
              disabled={busy}
              onClick={() => void start()}
            >
              Start a maze
            </Pressable>
          </div>
        </div>
      )}

      {match && round && (
        <div id="ham-play" data-state={match.state}>
          <p className="game-status" id="ham-banner">
            Round {round.round} of {match.roundCount} — you are the {round.role === 'walker' ? 'Walker' : 'Navigator'}.
          </p>
          <p className="game-hint" id="ham-par">
            Par is {round.par} moves. So far: {round.moves} moves, {round.bumps} walls, {round.locates} locates.
          </p>

          {round.role === 'walker' ? (
            <WalkerBoard fog={round.fog} size={round.size} walker={walkerCell} />
          ) : round.maze ? (
            <NavigatorBoard maze={round.maze} />
          ) : (
            <p className="game-hint">The map did not arrive — reload to catch up.</p>
          )}

          {round.role === 'walker' && (
            <p className="game-hint ham-felt" id="ham-felt">
              {feltCell
                ? feltSides.length > 0
                  ? `You can feel a wall to your ${feltSides.map(side => SIDE_NAME[side]).join(' and ')}.`
                  : 'All four ways are open from here.'
                : 'Feeling for walls…'}
            </p>
          )}

          {round.role === 'walker' && (
            <div className="ham-pad" role="group" aria-label="Walk">
              {ARROWS.map(a => (
                <button
                  key={a.dir}
                  type="button"
                  id={`ham-move-${a.dir}`}
                  className="game-cat ham-move"
                  data-dir={a.dir}
                  disabled={busy || !round.canMove || Boolean(round.settledAt)}
                  onClick={() => void move(a.dir)}
                >
                  {a.glyph} {a.label}
                </button>
              ))}
            </div>
          )}

          {round.role === 'walker' && (
            <p className="game-status ham-move-note" id="ham-move-note" aria-live="polite">
              {moveNote ?? 'One step per press. The thick lines are walls; ● is you.'}
            </p>
          )}

          {round.role === 'navigator' && (
            <div className="game-actions">
              <Pressable
                className="pear-btn pear-btn-blush"
                id="ham-reveal"
                data-reveals-left={String(round.revealsLeft)}
                disabled={busy || !round.canReveal || Boolean(round.settledAt)}
                onClick={() => void reveal()}
              >
                {round.canReveal
                  ? `Reveal where they are (${round.revealsLeft} of ${REVEALS_PER_ROUND} left)`
                  : 'Both locates spent this round'}
              </Pressable>
            </div>
          )}

          {round.role === 'navigator' && round.reveal && revealSecondsLeft != null && (
            <p className="game-status" id="ham-reveal-live">
              They are at {round.reveal.row + 1},{round.reveal.col + 1} for {revealSecondsLeft}s more.
            </p>
          )}

          <div className="ham-chat" id="ham-chat">
            <p className="game-hint ham-chat-label">
              Between you ({round.chatLeft} lines left this round)
            </p>
            {round.chat.length === 0 ? (
              <p className="game-hint" id="ham-chat-empty">
                Nothing said yet. The Walker has to describe what they feel.
              </p>
            ) : (
              <ul className="ham-lines" role="log" aria-label="What you have said">
                {round.chat.map((line: HalfAMapChatLine, i) => (
                  <li className="ham-line" key={`${line.at}-${i}`}>
                    <span className="ham-line-who">{nameOf(line.memberId)}</span> {line.text}
                  </li>
                ))}
              </ul>
            )}
            {!round.settledAt && round.chatLeft > 0 && (
              <form className="ham-form" onSubmit={(e) => void send(e)}>
                <label className="game-hint" htmlFor="ham-chat-input">
                  Say something
                </label>
                <input
                  id="ham-chat-input"
                  className="game-input ham-input"
                  value={chatDraft}
                  onChange={e => setChatDraft(e.target.value.slice(0, CHAT_MAX))}
                  maxLength={CHAT_MAX}
                  autoComplete="off"
                  disabled={busy}
                />
                <div className="game-actions">
                  <span className="game-hint" id="ham-chat-count">
                    {chatDraft.length}/{CHAT_MAX}
                  </span>
                  <Pressable
                    className="pear-btn pear-btn-mustard"
                    id="btn-ham-send"
                    disabled={busy || chatDraft.trim().length === 0}
                  >
                    Send
                  </Pressable>
                </div>
              </form>
            )}
          </div>

          {round.settledAt != null && (
            <div id="ham-settle">
              <p className="game-status" id="ham-settle-line">
                Round {round.round} done — {round.score ?? 0} points
                {round.terms ? ` (${termLines(round).join(', ')})` : ''}.
              </p>
              <p className="game-hint">
                {round.round < match.roundCount
                  ? 'Roles swap for the next round. Same maze each round is a new one.'
                  : match.state === 'complete'
                    ? `That is all ${match.roundCount} rounds — ${match.totalScore} points between you.`
                    : 'Next round is next.'}
              </p>
            </div>
          )}

          {match.history.length > 0 && (
            <ul className="ham-history" id="ham-history" aria-label="Rounds so far">
              {match.history.map(h => (
                <li key={h.round} className="ham-history-row">
                  Round {h.round}: {h.score ?? '—'} points, {h.moves} moves, {h.bumps} walls
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {match && !round && <p className="game-hint">No round in play — start a maze.</p>}

      {error && (
        <p className="game-error" role="alert" id="ham-error">
          {error}
        </p>
      )}
    </section>
  );
}
